import React from 'react';
import { Button } from './ui/button';
import { Share2, Copy, Users, MessageSquare } from 'lucide-react';
import type { Decision } from '../types/decision';
import type { Participant } from '../types/participant';
import { useToast } from './ui/use-toast';
import { useNavigate } from 'react-router-dom';
import Analytics from '../services/analytics';

interface ResponseActionsProps {
  decisionId: string;
  responseContent: string;
  participantCount: number;
  isComplete?: boolean;
  decision: Decision;
  onShare?: () => void;
  participant?: Participant | null; // Add participant prop
}

export default function ResponseActions({
  decisionId,
  responseContent,
  participantCount,
  isComplete = false,
  decision,
  onShare,
  participant
}: ResponseActionsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleCopyResponse = async () => {
    try {
      const formattedResponse = `Question: ${decision.question}
  
${decision.details ? `Context: ${decision.details}\n\n` : ''}Group Response (from ${participantCount} participants):\n
${responseContent}
  
View full discussion: ${window.location.origin}/d/${decisionId}`;
  
      await navigator.clipboard.writeText(formattedResponse);
      Analytics.trackResponseShared(decisionId, 'copy');
      toast({
        title: "Copied to clipboard",
        description: "Response with context has been copied",
      });
    } catch (err) {
      console.error('Failed to copy response:', err);
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please try again",
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      const responseUrl = `${window.location.origin}/d/${decisionId}`;
      await navigator.clipboard.writeText(responseUrl);
      Analytics.trackResponseShared(decisionId, 'link');
      toast({
        title: "Copied to clipboard",
        description: "Link has been copied",
      });
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please try again",
      });
    }
  };

  const handleContinueDiscussion = () => {

    Analytics.trackEvent('continue_discussion', { 
      decisionId, 
      fromResponse: true 
    });
    
    navigate(`/d/${decisionId}/chat`);
  };

  // Should we show the continue discussion button?
  const showContinueButton = decision.status === 'active' && 
    participant && 
    participant.status !== 'completed';

  return (
    <div className="flex flex-col gap-2 md:flex-row md:justify-end">
      {/* Continue Discussion button if participant exists and hasn't completed */}
      {showContinueButton && (
        <Button 
          variant="outline" 
          onClick={handleContinueDiscussion}
          className="w-full md:w-auto"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Continue Chat
        </Button>
      )}
      
      {isComplete ? (
        // For completed decisions, focus on sharing the outcome
        <>
          <Button 
            variant="outline" 
            onClick={handleCopyResponse}
            className="w-full md:w-auto"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Response
          </Button>
          <Button 
            onClick={handleCopyLink}
            className="w-full md:w-auto"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Response
          </Button>
        </>
      ) : (
        // For active decisions, prioritize getting more participants
        <>
        <Button 
          variant="outline" 
          onClick={handleContinueDiscussion}
          className="w-full md:w-auto"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Continue Chat
        </Button>
          <Button 
            variant="outline" 
            onClick={handleCopyResponse}
            className="w-full md:w-auto"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Response
          </Button>
          <Button 
            onClick={onShare}
            className="w-full md:w-auto"
          >
            <Users className="h-4 w-4 mr-2" />
            Invite Participants
          </Button>
        </>
      )}
    </div>
  );
}